import {
  buildProvenanceFlags,
  buildProvenanceRecord,
  toSafeMetadata,
  type BuildProvenanceInput,
} from '../operations/resolve';
import { getAITransparencyFlags } from '../config/flags';
import { createProvenanceProvider } from './providers';
import { logProvenanceEvent } from './logging';
import type { AIProvenanceFlags, AIProvenanceRecord, ProvenanceAsset } from '../types';

export type RecordJobProvenanceResult = {
  record: AIProvenanceRecord;
  flags: AIProvenanceFlags;
  metadata: Record<string, unknown>;
  machineReadableApplied: boolean;
};

/**
 * Primary entry point for job completion: build trusted provenance,
 * optionally attempt machine-readable embedding, return DB-ready flags.
 * Never throws for provenance failures.
 */
export async function recordJobProvenance(
  input: BuildProvenanceInput & { asset?: ProvenanceAsset },
): Promise<RecordJobProvenanceResult> {
  const flagsEnv = getAITransparencyFlags({
    aiTransparencyEnabled: input.transparencyEnabled,
    aiProvenanceEnabled: input.provenanceEnabled,
  });

  if (!flagsEnv.aiTransparencyEnabled) {
    const record = buildProvenanceRecord({
      ...input,
      visibleDisclosure: false,
      machineReadable: false,
      transparencyEnabled: false,
    });
    const flags = buildProvenanceFlags(record, {
      machineReadableApplied: false,
      transparencyEnabled: false,
      provenanceEnabled: false,
    });
    return { record, flags, metadata: {}, machineReadableApplied: false };
  }

  const record = buildProvenanceRecord({
    ...input,
    transparencyEnabled: flagsEnv.aiTransparencyEnabled,
    provenanceEnabled: flagsEnv.aiProvenanceEnabled,
  });

  let machineReadableApplied = false;
  let provenanceFailed = false;
  let provenanceFailureReason: string | null = null;

  if (
    flagsEnv.aiProvenanceEnabled &&
    record.disclosure.machineReadable &&
    input.asset
  ) {
    const provider = createProvenanceProvider({
      c2paSigningEnabled: flagsEnv.c2paSigningEnabled,
    });
    try {
      const result = await provider.apply(input.asset, record);
      machineReadableApplied = result.applied;
      if (!result.applied && flagsEnv.c2paSigningEnabled) {
        provenanceFailed = true;
        provenanceFailureReason = result.reason ?? 'provenance_not_applied';
      }
    } catch (err) {
      provenanceFailed = true;
      provenanceFailureReason = err instanceof Error ? err.message : 'provenance_error';
      machineReadableApplied = false;
    }
  }

  const flags = buildProvenanceFlags(record, {
    machineReadableApplied,
    provenanceFailed,
    provenanceFailureReason,
    transparencyEnabled: flagsEnv.aiTransparencyEnabled,
    provenanceEnabled: flagsEnv.aiProvenanceEnabled,
  });

  logProvenanceEvent({
    event: 'ai_transparency_recorded',
    app: record.app,
    assetId: record.outputAssetId,
    classification: record.classification,
    operation: record.operation,
    success: !provenanceFailed,
    reason: provenanceFailureReason ?? undefined,
  });

  return {
    record,
    flags,
    metadata: toSafeMetadata(record, flags),
    machineReadableApplied,
  };
}
